import { api } from "../api/Middleware";
import {
  API_URL,
  PRODUCTS_URL,
  PRODUCTS_IMAGES_URL,
  PRODUCTS_STATS_URL,
} from "../api/Urls";
import { currencyToFloat } from "../helpers/Helpers";

// =============================
// PRODUCTS SERVICES
// =============================

// Listar produtos com paginação e filtros
export const listProducts = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Parâmetros de paginação
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    // Parâmetros de busca e filtro
    if (params.search) queryParams.append('search', params.search);
    if (params.agent_id) queryParams.append('agent_id', params.agent_id);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    
    const url = `${PRODUCTS_URL}by_agent/?${queryParams.toString()}`;
    
    const response = await api.get(url);
    
    return {
      success: true,
      data: response.data,
      products: response.data.results || response.data,
      count: response.data.count || 0,
      total_pages: response.data.total_pages || Math.ceil((response.data.count || 0) / (params.page_size || 10)),
      current_page: params.page || 1,
    };
  } catch (error) {
    console.error('Erro ao listar produtos:', error);
    
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao carregar produtos",
      errorCode: error.response?.data?.error_code || "UNKNOWN_ERROR",
      products: [],
      count: 0,
      total_pages: 1,
      current_page: 1,
    };
  }
};

// Buscar produto específico
export const getProduct = async (id) => {
  try {
    const response = await api.get(`${PRODUCTS_URL}${id}/`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Erro ao buscar produto:', error);
    return {
      success: false,
      error: error.response?.data?.error || "Produto não encontrado",
      errorCode: error.response?.data?.error_code || "NOT_FOUND",
    };
  }
};

// Criar novo produto (pode incluir imagens)
export const createProduct = async (data, imageFiles = []) => {
  let requestData;
  let requestHeaders = {};
  
  try {
    console.log('=== DEBUG CRIAR PRODUTO ===');
    console.log('API_URL base:', API_URL);
    console.log('PRODUCTS_URL completa:', PRODUCTS_URL);
    console.log('Dados enviados:', data);
    console.log('Arquivos de imagem:', imageFiles?.length || 0);
    
    // Se há imagens, usar FormData para enviar tudo junto
    if (imageFiles && imageFiles.length > 0) {
      console.log('Criando FormData com imagens...');
      requestData = new FormData();
      
      // Adicionar dados do produto
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined) {
          requestData.append(key, data[key]);
          console.log(`FormData - ${key}:`, data[key]);
        }
      });
      
      // Adicionar imagens
      imageFiles.forEach((file, index) => {
        console.log(`Adicionando imagem ${index + 1}:`, {
          name: file.name,
          size: file.size,
          type: file.type
        });
        requestData.append('images', file);
      });
      
      // Para FormData, não definir Content-Type (deixar o browser definir com boundary)
      // requestHeaders['Content-Type'] = 'multipart/form-data'; // NÃO fazer isso!
      
      console.log('FormData criado com', imageFiles.length, 'imagens');
    } else {
      // Sem imagens, enviar JSON normal
      console.log('Enviando dados JSON sem imagens...');
      requestData = data;
      requestHeaders['Content-Type'] = 'application/json';
    }
    
    const response = await api.post(PRODUCTS_URL, requestData, {
      headers: requestHeaders
    });
    
    console.log('Resposta da API:', response);
    console.log('Status:', response.status);
    console.log('Data:', response.data);
    
    return {
      success: true,
      data: response.data,
      imagesUploaded: imageFiles?.length || 0,
    };
  } catch (error) {
    console.error('=== ERRO AO CRIAR PRODUTO ===');
    console.error('Erro completo:', error);
    console.error('Response status:', error.response?.status);
    console.error('Response data:', error.response?.data);
    
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao criar produto",
      errorCode: error.response?.data?.error_code || "CREATE_ERROR",
      errors: error.response?.data?.errors || {},
      errorMessage: error.response?.data?.message || "Erro ao criar produto",
    };
  }
};

// Função mantida para compatibilidade, mas agora usa o createProduct atualizado
export const createProductWithImages = async (productData, imageFiles = []) => {
  console.log('=== createProductWithImages (usando createProduct atualizado) ===');
  return await createProduct(productData, imageFiles);
};

// Atualizar produto existente (com suporte a imagens)
export const updateProduct = async (id, data, imageFiles = []) => {
  try {
    console.log('=== DEBUG UPDATE PRODUCT ===');
    console.log('Product ID:', id);
    console.log('Data:', data);
    console.log('Image Files:', imageFiles?.length || 0);
    
    if (imageFiles && imageFiles.length > 0) {
      // Usar FormData quando há imagens
      const formData = new FormData();
      
      // Adicionar dados do produto
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });
      
      // Adicionar imagens
      imageFiles.forEach((file, index) => {
        console.log(`Adicionando imagem ${index + 1} ao update:`, {
          name: file.name,
          size: file.size,
          type: file.type
        });
        formData.append('images', file);
      });
      
      // Debug FormData
      for (let pair of formData.entries()) {
        console.log('FormData entry:', pair[0], pair[1]);
      }
      
      const response = await api.put(`${PRODUCTS_URL}${id}/`, formData);
      return {
        success: true,
        data: response.data,
      };
    } else {
      // Usar JSON quando não há imagens
      const response = await api.put(`${PRODUCTS_URL}${id}/`, data);
      return {
        success: true,
        data: response.data,
      };
    }
  } catch (error) {
    console.error('Erro ao atualizar produto:', error);
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao atualizar produto",
      errorCode: error.response?.data?.error_code || "UPDATE_ERROR",
      errors: error.response?.data?.errors || {},
    };
  }
};

// Atualizar parcialmente produto
export const patchProduct = async (id, data) => {
  try {
    const response = await api.patch(`${PRODUCTS_URL}${id}/`, data);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Erro ao atualizar produto:', error);
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao atualizar produto",
      errorCode: error.response?.data?.error_code || "PATCH_ERROR",
      errors: error.response?.data?.errors || {},
    };
  }
};

// Deletar produto
export const deleteProduct = async (id) => {
  try {
    await api.delete(`${PRODUCTS_URL}${id}/`);
    return {
      success: true,
    };
  } catch (error) {
    console.error('Erro ao deletar produto:', error);
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao deletar produto",
      errorCode: error.response?.data?.error_code || "DELETE_ERROR",
    };
  }
};

// Buscar produtos por agente
export const getProductsByAgent = async (agentId, params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('agent_id', agentId);
    
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    if (params.search) queryParams.append('search', params.search);
    
    const response = await api.get(`${PRODUCTS_URL}?${queryParams.toString()}`);
    
    return {
      success: true,
      data: response.data,
      products: response.data.results || response.data,
      count: response.data.count || 0,
    };
  } catch (error) {
    console.error('Erro ao buscar produtos por agente:', error);
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao carregar produtos do agente",
      products: [],
      count: 0,
    };
  }
};

// Obter estatísticas de produtos
export const getProductStats = async () => {
  try {
    const response = await api.get(PRODUCTS_STATS_URL);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Erro ao buscar estatísticas de produtos:', error);
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao carregar estatísticas",
      data: {
        total_products: 0,
        active_products: 0,
        products_by_agent: 0,
        total_value: 0,
      },
    };
  }
};

// =============================
// PRODUCTS IMAGES SERVICES
// =============================

// Upload de múltiplas imagens para produto
export const uploadProductImages = async (productId, files) => {
  try {
    console.log('=== DEBUG UPLOAD IMAGENS ===');
    console.log('Product ID:', productId);
    console.log('Arquivos:', files?.length || 0);
    console.log('URL de upload:', PRODUCTS_IMAGES_URL);
    
    if (!files || files.length === 0) {
      return {
        success: true,
        data: { images: [] },
        images: [],
      };
    }
    
    const formData = new FormData();
    formData.append("agent_product", productId);
    
    // Adicionar cada arquivo
    files.forEach((file, index) => {
      console.log(`Adicionando arquivo ${index + 1}:`, {
        name: file.name,
        size: file.size,
        type: file.type
      });
      formData.append('images', file);
    });
    
    // Debug do FormData
    console.log('FormData criado com product_id:', productId);
    for (let pair of formData.entries()) {
      console.log('FormData entry:', pair[0], pair[1]);
    }
    
    const response = await api.post(PRODUCTS_IMAGES_URL, formData);
    
    console.log('Resposta do upload:', response.data);
    
    return {
      success: true,
      data: response.data,
      images: response.data.images || response.data.results || [],
    };
  } catch (error) {
    console.error('=== ERRO NO UPLOAD DE IMAGENS ===');
    console.error('Erro completo:', error);
    console.error('Response status:', error.response?.status);
    console.error('Response data:', error.response?.data);
    console.error('Response headers:', error.response?.headers);
    
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao fazer upload das imagens",
      errorCode: error.response?.data?.error_code || "UPLOAD_ERROR",
      images: [],
      debug: {
        url: PRODUCTS_IMAGES_URL,
        method: 'POST',
        status: error.response?.status,
        productId: productId,
        filesCount: files?.length || 0,
      }
    };
  }
};

// Listar imagens do produto
export const getProductImages = async (productId) => {
  try {
    const response = await api.get(`${PRODUCTS_IMAGES_URL}?product_id=${productId}`);
    return {
      success: true,
      data: response.data,
      images: response.data.results || response.data,
    };
  } catch (error) {
    console.error('Erro ao buscar imagens do produto:', error);
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao carregar imagens",
      images: [],
    };
  }
};

// Deletar imagem do produto
export const deleteProductImage = async (productId, imageId) => {
  try {
    await api.delete(`${PRODUCTS_IMAGES_URL}${imageId}/`);
    return {
      success: true,
    };
  } catch (error) {
    console.error('Erro ao deletar imagem:', error);
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao deletar imagem",
      errorCode: error.response?.data?.error_code || "DELETE_IMAGE_ERROR",
    };
  }
};

// =============================
// HELPER FUNCTIONS
// =============================

// Função auxiliar para criar FormData com múltiplas imagens
export const createProductImagesFormData = (productId, files) => {
  const formData = new FormData();
  formData.append('product_id', productId);
  
  files.forEach((file) => {
    formData.append('images', file);
  });
  
  return formData;
};

// Validar arquivo de imagem antes do upload
export const validateProductImage = (file, options = {}) => {
  const errors = [];
  const warnings = [];
  
  const config = {
    maxSize: options.maxSize || 3 * 1024 * 1024, // 3MB
    allowedTypes: options.allowedTypes || ['image/jpeg', 'image/jpg', 'image/png'],
    maxNameLength: options.maxNameLength || 255,
    ...options
  };
  
  if (!file) {
    errors.push("Arquivo não fornecido");
    return { isValid: false, errors, warnings };
  }
  
  // Validar tamanho
  if (file.size > config.maxSize) {
    errors.push(`Imagem muito grande: ${(file.size / (1024 * 1024)).toFixed(2)}MB (máximo: ${(config.maxSize / (1024 * 1024)).toFixed(2)}MB)`);
  }
  
  // Validar tipo
  if (!config.allowedTypes.includes(file.type)) {
    errors.push(`Tipo de imagem não suportado: ${file.type}`);
  }
  
  // Validar nome
  if (file.name.length > config.maxNameLength) {
    errors.push(`Nome do arquivo muito longo (máximo: ${config.maxNameLength} caracteres)`);
  }
  
  // Avisos
  if (file.size > 1024 * 1024) { // > 1MB
    warnings.push(`Imagem grande (${(file.size / (1024 * 1024)).toFixed(2)}MB) pode demorar para carregar`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    fileInfo: {
      name: file.name,
      size: file.size,
      type: file.type,
      sizeFormatted: formatFileSize(file.size)
    }
  };
};

// Validar múltiplas imagens
export const validateProductImages = (files, options = {}) => {
  const maxFiles = options.maxFiles || 5;
  const results = [];
  
  if (files.length > maxFiles) {
    return {
      isValid: false,
      globalErrors: [`Máximo de ${maxFiles} imagens permitidas`],
      results: []
    };
  }
  
  let globalErrors = [];
  let totalSize = 0;
  
  files.forEach((file, index) => {
    const validation = validateProductImage(file, options);
    totalSize += file.size;
    results.push({
      index,
      file,
      ...validation
    });
  });
  
  // Verificar tamanho total
  const maxTotalSize = options.maxTotalSize || 15 * 1024 * 1024; // 15MB total
  if (totalSize > maxTotalSize) {
    globalErrors.push(`Tamanho total das imagens muito grande: ${(totalSize / (1024 * 1024)).toFixed(2)}MB (máximo: ${(maxTotalSize / (1024 * 1024)).toFixed(2)}MB)`);
  }
  
  const allValid = results.every(r => r.isValid) && globalErrors.length === 0;
  
  return {
    isValid: allValid,
    globalErrors,
    results,
    totalSize,
    totalSizeFormatted: formatFileSize(totalSize)
  };
};

// Preparar dados do produto para envio (atualizado para trabalhar com FormData quando necessário)
export const prepareProductData = (formData, options = {}) => {
  console.log('=== DEBUG PREPARE PRODUCT DATA ===');
  console.log('FormData recebido:', formData);
  console.log('Options:', options);
  
  // Validar dados obrigatórios
  if (!formData.agent && !formData.agent?.value) {
    console.error('ERRO: Agent não fornecido ou inválido');
    throw new Error('Agente é obrigatório');
  }
  
  if (!formData.name || !formData.name.trim()) {
    console.error('ERRO: Nome não fornecido ou vazio');
    throw new Error('Nome do produto é obrigatório');
  }
  
  // Converter valor formatado para float
  const numericValue = currencyToFloat(formData.value);
  if (!formData.value || numericValue <= 0) {
    console.error('ERRO: Valor inválido:', formData.value);
    throw new Error('Valor do produto deve ser maior que zero');
  }
  
  const data = {
    agent: formData.agent?.value || formData.agent,
    name: formData.name?.trim(),
    value: numericValue,
  };
  
  // Adicionar campos opcionais apenas se não estiverem vazios
  if (formData.description?.trim()) {
    data.description = formData.description.trim();
  }
  
  if (formData.link?.trim()) {
    data.link = formData.link.trim();
  }
  
  // Campo order foi removido
  
  console.log('Dados finais preparados:', data);
  console.log('JSON final:', JSON.stringify(data));
  
  return data;
};

// Formatar dados do produto para exibição
export const formatProductData = (product) => {
  if (!product) return null;
  
  return {
    ...product,
    formattedCreatedAt: formatDate(product.created_at),
    formattedUpdatedAt: formatDate(product.updated_at),
    formattedValue: formatCurrency(product.value),

    imageCount: product.images?.length || 0,
    hasImages: (product.images?.length || 0) > 0,
    agentName: product.agent_name || product.agent?.name || 'N/A',
  };
};

// Construir parâmetros de busca
export const buildSearchParams = (filters) => {
  const params = new URLSearchParams();
  
  Object.keys(filters).forEach(key => {
    const value = filters[key];
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString());
    }
  });
  
  return params;
};

// Processar erro de produto com nova estrutura amigável da API
export const processProductError = (error) => {
  const errorData = error.response?.data || {};
  const status = error.response?.status;
  
  // Nova estrutura amigável da API
  const errorTitle = errorData.error || 'Erro desconhecido';
  const errorMessage = errorData.message || 'Ocorreu um erro inesperado';
  const errorCode = errorData.error_code;
  
  let errorInfo = {
    type: 'generic',
    title: errorTitle,
    message: errorMessage,
    details: {},
    code: errorCode
  };
  
  // Mapear códigos de erro específicos para tipos
  if (errorCode) {
    switch (errorCode) {
      case 'PRODUCT_LIMIT_EXCEEDED':
        errorInfo.type = 'limit_exceeded';
        break;
      case 'INVALID_AGENT':
      case 'AGENT_NOT_FOUND':
      case 'MISSING_AGENT_ID':
        errorInfo.type = 'agent_error';
        break;
      case 'INVALID_NAME':
      case 'INVALID_VALUE':
      case 'VALIDATION_ERROR':
        errorInfo.type = 'validation';
        break;
      case 'IMAGE_LIMIT_EXCEEDED':
      case 'INVALID_IMAGE_FILE':
        errorInfo.type = 'image_error';
        break;
      case 'PRODUCT_NOT_FOUND':
      case 'IMAGE_NOT_FOUND':
        errorInfo.type = 'not_found';
        break;
      default:
        errorInfo.type = 'generic';
    }
  } else {
    // Fallback para estrutura antiga ou casos sem error_code
    if (status === 400) {
      errorInfo.type = 'validation';
      // Verificar se há erros de validação detalhados
      if (errorData.errors) {
        errorInfo.details = errorData.errors;
        errorInfo.message = 'Verifique os dados informados e corrija os erros abaixo.';
      }
    } else if (status === 403) {
      errorInfo.type = 'permission';
      errorInfo.title = 'Acesso negado';
      errorInfo.message = 'Você não tem permissão para realizar esta ação.';
    } else if (status === 404) {
      errorInfo.type = 'not_found';
      errorInfo.title = 'Não encontrado';
      errorInfo.message = 'O recurso solicitado não foi encontrado.';
    } else if (status === 500) {
      errorInfo.type = 'server_error';
      errorInfo.title = 'Erro interno';
      errorInfo.message = 'Ocorreu um erro interno do servidor. Tente novamente em alguns minutos.';
    }
  }
  
  return errorInfo;
};

// Função auxiliar para exibir erros amigáveis ao usuário
export const showErrorAlert = (error, customTitle = null) => {
  const errorInfo = processProductError(error);
  
  // Configuração básica do alerta
  const alertConfig = {
    icon: getErrorIcon(errorInfo.type),
    title: customTitle || errorInfo.title,
    confirmButtonColor: getErrorButtonColor(errorInfo.type),
    width: 500,
  };

  // Personalizar mensagem baseada no tipo de erro
  switch (errorInfo.type) {
    case 'limit_exceeded':
      alertConfig.html = `
        <div style="text-align: left; padding: 10px;">
          <p><strong>${errorInfo.message}</strong></p>
          <hr>
          <p class="text-warning">
            <i class="las la-exclamation-triangle"></i>
            Para adicionar mais produtos, considere fazer upgrade do seu plano.
          </p>
        </div>
      `;
      break;

    case 'agent_error':
      alertConfig.html = `
        <div style="text-align: left; padding: 10px;">
          <p><strong>${errorInfo.message}</strong></p>
          <hr>
          <p class="text-info">
            <i class="las la-info-circle"></i>
            Verifique se o agente selecionado está ativo e pertence à sua conta.
          </p>
        </div>
      `;
      break;

    case 'validation':
      if (errorInfo.details && Object.keys(errorInfo.details).length > 0) {
        const validationErrors = Object.entries(errorInfo.details)
          .map(([field, errors]) => `<li><strong>${getFieldLabel(field)}:</strong> ${Array.isArray(errors) ? errors.join(', ') : errors}</li>`)
          .join('');
        
        alertConfig.html = `
          <div style="text-align: left; padding: 10px;">
            <p><strong>${errorInfo.message}</strong></p>
            <ul style="margin: 10px 0;">${validationErrors}</ul>
          </div>
        `;
      } else {
        alertConfig.text = errorInfo.message;
      }
      break;

    case 'image_error':
      alertConfig.html = `
        <div style="text-align: left; padding: 10px;">
          <p><strong>${errorInfo.message}</strong></p>
          <hr>
          <p class="text-warning">
            <i class="las la-image"></i>
            Formatos aceitos: JPG, JPEG, PNG (máximo 3MB cada)
          </p>
        </div>
      `;
      break;

    case 'permission':
      alertConfig.html = `
        <div style="text-align: left; padding: 10px;">
          <p><strong>${errorInfo.message}</strong></p>
          <hr>
          <p class="text-info">
            <i class="las la-shield-alt"></i>
            Entre em contato com o administrador se precisar de mais permissões.
          </p>
        </div>
      `;
      break;

    case 'server_error':
      alertConfig.html = `
        <div style="text-align: left; padding: 10px;">
          <p><strong>${errorInfo.message}</strong></p>
          <hr>
          <p class="text-info">
            <i class="las la-clock"></i>
            Se o problema persistir, entre em contato com o suporte.
          </p>
        </div>
      `;
      break;

    default:
      alertConfig.text = errorInfo.message;
  }

  return alertConfig;
};

// Função para determinar o ícone baseado no tipo de erro
const getErrorIcon = (errorType) => {
  switch (errorType) {
    case 'limit_exceeded':
      return 'warning';
    case 'validation':
    case 'image_error':
      return 'error';
    case 'permission':
      return 'warning';
    case 'not_found':
      return 'question';
    case 'server_error':
      return 'error';
    default:
      return 'error';
  }
};

// Função para determinar a cor do botão baseada no tipo de erro
const getErrorButtonColor = (errorType) => {
  switch (errorType) {
    case 'limit_exceeded':
      return '#f39c12';
    case 'validation':
    case 'image_error':
      return '#dc3545';
    case 'permission':
      return '#fd7e14';
    case 'not_found':
      return '#17a2b8';
    case 'server_error':
      return '#dc3545';
    default:
      return '#dc3545';
  }
};

// Função para mapear nomes de campos para labels amigáveis
const getFieldLabel = (fieldName) => {
  const fieldLabels = {
    'agent': 'Agente',
    'name': 'Nome do Produto',
    'value': 'Valor',
    'description': 'Descrição',
    'link': 'Link',
    'order': 'Ordem',
    'images': 'Imagens',
  };
  
  return fieldLabels[fieldName] || fieldName;
};

// Função de demonstração para testar diferentes tipos de erro (apenas para desenvolvimento)
export const testErrorTypes = () => {
  const errorExamples = {
    limit_exceeded: {
      response: {
        data: {
          error: "Limite de produtos atingido para este agente",
          message: "Este agente já atingiu o limite máximo de produtos permitido pelo seu plano.",
          error_code: "PRODUCT_LIMIT_EXCEEDED"
        }
      }
    },
    invalid_agent: {
      response: {
        data: {
          error: "Agente inválido",
          message: "O agente selecionado não pertence à sua conta.",
          error_code: "INVALID_AGENT"
        }
      }
    },
    validation_error: {
      response: {
        data: {
          error: "Nome inválido",
          message: "O nome do produto é obrigatório e deve ter pelo menos 3 caracteres.",
          error_code: "INVALID_NAME"
        }
      }
    },
    image_limit: {
      response: {
        data: {
          error: "Limite de imagens atingido",
          message: "Este produto já possui o máximo de 5 imagens permitidas.",
          error_code: "IMAGE_LIMIT_EXCEEDED"
        }
      }
    },
    server_error: {
      response: {
        status: 500,
        data: {
          error: "Erro interno do servidor",
          message: "Ocorreu um erro interno. Tente novamente em alguns minutos.",
          error_code: "INTERNAL_SERVER_ERROR"
        }
      }
    }
  };
  
  return errorExamples;
};

// Obter filtros disponíveis para a interface
export const getAvailableFilters = () => {
  return {
    ordering: [
      { value: '-created_at', label: 'Mais recentes' },
      { value: 'created_at', label: 'Mais antigos' },
      { value: 'name', label: 'Nome (A-Z)' },
      { value: '-name', label: 'Nome (Z-A)' },
      { value: '-value', label: 'Maior valor' },
      { value: 'value', label: 'Menor valor' },
    ],
  };
};

// =============================
// UTILITY FUNCTIONS
// =============================

// Formatar moeda
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// Formatar data
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } catch (error) {
    return 'Data inválida';
  }
};

// Formatar tamanho de arquivo
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// =============================
// IMPORTAÇÃO DE PRODUTOS
// =============================

// Importar produtos via CSV/XLSX
export const importProducts = async (file, agentId) => {
  try {
    console.log('=== DEBUG IMPORT PRODUCTS ===');
    console.log('File:', file);
    console.log('Agent ID:', agentId);

    if (!file) {
      throw new Error('Arquivo é obrigatório');
    }

    if (!agentId) {
      throw new Error('Agente é obrigatório');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('agent_id', agentId);

    const response = await api.post(PRODUCTS_URL + "import_products/", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('Import response:', response.data);

    if (response.data) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      throw new Error('Resposta inválida da API');
    }
  } catch (error) {
    console.error('Erro na importação de produtos:', error);
    
    if (error.response?.data) {
      return {
        success: false,
        error: error.response.data.message || error.response.data.error || 'Erro na importação',
        details: error.response.data,
      };
    }
    
    return {
      success: false,
      error: error.message || 'Erro na importação de produtos',
    };
  }
};

// Baixar arquivo exemplo para importação
export const downloadImportSample = async () => {
  try {
    console.log('=== DEBUG DOWNLOAD SAMPLE ===');

    const response = await api.get(PRODUCTS_URL + "import_sample/", {
      responseType: 'blob', // Importante para arquivos
    });

    console.log('Sample download response:', response);

    if (response.data) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      throw new Error('Resposta inválida da API');
    }
  } catch (error) {
    console.error('Erro ao baixar arquivo exemplo:', error);
    
    if (error.response?.data) {
      return {
        success: false,
        error: error.response.data.message || error.response.data.error || 'Erro ao baixar arquivo',
        details: error.response.data,
      };
    }
    
    return {
      success: false,
      error: error.message || 'Erro ao baixar arquivo exemplo',
    };
  }
};

// Exclusão em massa de produtos
export const bulkDeleteProducts = async (productIds) => {
  try {
    console.log('=== DEBUG BULK DELETE PRODUCTS ===');
    console.log('Product IDs:', productIds);

    if (!productIds || !Array.isArray(productIds) || productIds.length === 0) {
      throw new Error('Lista de IDs é obrigatória');
    }

    // Validar limite máximo de 100 produtos
    if (productIds.length > 100) {
      throw new Error('Máximo de 100 produtos por requisição');
    }

    // Validar se todos os IDs são números válidos
    const validIds = productIds.filter(id => Number.isInteger(id) && id > 0);
    if (validIds.length !== productIds.length) {
      throw new Error('Todos os IDs devem ser números válidos');
    }

    const requestData = {
      ids: validIds
    };

    const response = await api.delete(PRODUCTS_URL + "bulk_delete/", {
      data: requestData,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('Bulk delete response:', response.data);

    if (response.data) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      throw new Error('Resposta inválida da API');
    }
  } catch (error) {
    console.error('Erro na exclusão em massa:', error);
    
    if (error.response?.data) {
      return {
        success: false,
        error: error.response.data.message || error.response.data.error || 'Erro na exclusão em massa',
        details: error.response.data,
      };
    }
    
    return {
      success: false,
      error: error.message || 'Erro na exclusão em massa de produtos',
    };
  }
};

// =============================
// TREINAMENTO RAG DE AGENTES
// =============================

// Treinar agente com RAG usando produtos ativos
export const trainAgentRAG = async (agentId) => {
  try {
    console.log('=== DEBUG TRAIN AGENT RAG ===');
    console.log('Agent ID:', agentId);

    if (!agentId) {
      throw new Error('ID do agente é obrigatório');
    }

    // Validar se o agentId é um número válido
    const validAgentId = Number(agentId);
    if (!Number.isInteger(validAgentId) || validAgentId <= 0) {
      throw new Error('ID do agente deve ser um número válido');
    }

    const requestData = {
      agent_id: validAgentId
    };

    const response = await api.post(PRODUCTS_URL + "train-agent-rag/", requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('Train agent RAG response:', response.data);

    if (response.data) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      throw new Error('Resposta inválida da API');
    }
  } catch (error) {
    console.error('Erro no treinamento RAG do agente:', error);
    
    if (error.response?.data) {
      return {
        success: false,
        error: error.response.data.message || error.response.data.error || 'Erro no treinamento do agente',
        errorCode: error.response.data.error_code || 'TRAINING_ERROR',
        details: error.response.data,
      };
    }
    
    return {
      success: false,
      error: error.message || 'Erro no treinamento RAG do agente',
      errorCode: 'NETWORK_ERROR',
    };
  }
};

// Exportar funções principais como default
const ProductsServices = {
  listProducts,
  getProduct,
  createProduct,
  createProductWithImages,
  updateProduct,
  patchProduct,
  deleteProduct,
  getProductsByAgent,
  getProductStats,
  uploadProductImages,
  getProductImages,
  deleteProductImage,
  createProductImagesFormData,
  validateProductImage,
  validateProductImages,
  prepareProductData,
  formatProductData,
  buildSearchParams,
  processProductError,
  showErrorAlert,
  testErrorTypes,
  getAvailableFilters,
  formatCurrency,
  formatDate,
  importProducts,
  downloadImportSample,
  bulkDeleteProducts,
  trainAgentRAG,
};

export default ProductsServices;
